import {
  Button,
  Form,
  InputNumber,
  InputCurrency,
} from "@sellar-io/components";
import { ReactNode, useEffect } from "react";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { Icon } from "../../../../../../components";
import { ItemDiscountType } from "../BusinessConnectionItemDiscountsCard.shared";
import { formatCurrency } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import { Control, useForm, useWatch } from "react-hook-form";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../../../../hooks/usePriceFormat";
import { calculatePercentageDiscount } from "@sellar-io/common";

type FormInputs = {
  discountAmount: number;
  type: "flat" | "percentage";
};

export type ItemDiscountFormSubmitType = {
  discountAmount: number;
  type: "flat" | "percentage";
};

const useCalculateProductItemDiscountAmountApi = ({
  priceApi,
  control,
}: {
  priceApi: number;
  control: Control<FormInputs>;
}) => {
  const { discountAmount, type } = useWatch({
    control,
  });

  if (!discountAmount || Number.isNaN(discountAmount)) {
    return 0;
  }

  if (type === "percentage") {
    return calculatePercentageDiscount(priceApi, discountAmount);
  }

  const discountAmountApi = formatPriceInputToApi(discountAmount) ?? 0;
  return discountAmountApi > priceApi ? priceApi : discountAmountApi;
};

const DiscountLeftCaption = ({
  containerTypeDiscountAmount,
  productItemDiscountAmount,
}: {
  containerTypeDiscountAmount: number;
  productItemDiscountAmount: number;
}) => {
  if (
    containerTypeDiscountAmount === 0 ||
    productItemDiscountAmount === 0 ||
    productItemDiscountAmount > containerTypeDiscountAmount
  ) {
    return null;
  }

  return (
    <div>
      Discount by{" "}
      {formatCurrency(
        containerTypeDiscountAmount - productItemDiscountAmount + 1,
        "GBP",
      )}{" "}
      more to override existing container type discount.
    </div>
  );
};

const ItemPriceRow = ({
  description,
  priceApi,
  style = "default",
}: {
  description: string;
  priceApi: number;
  style?: "default" | "bold" | "strike-through";
}) => {
  const styleType = {
    default: "",
    bold: twMerge("text-base font-semibold"),
    "strike-through": twMerge(
      "text-base font-normal text-zinc-500 line-through",
    ),
  };

  return (
    <div className={"flex items-center justify-between p-4"}>
      <div className={styleType[style]}>{description}</div>
      <div className={styleType[style]}>{formatCurrency(priceApi, "GBP")}</div>
    </div>
  );
};

const ItemPricePreview = ({
  itemPriceBeforeDiscount,
  containerTypeDiscountAmount,
  productItemDiscountAmount,
}: {
  itemPriceBeforeDiscount: number;
  containerTypeDiscountAmount: number;
  productItemDiscountAmount: number;
}) => {
  const priceWithContainerTypeDiscount =
    itemPriceBeforeDiscount - containerTypeDiscountAmount;

  const priceWithProductItemDiscount =
    itemPriceBeforeDiscount - productItemDiscountAmount;

  const containerTypeDiscountIsBigger =
    containerTypeDiscountAmount > productItemDiscountAmount;

  return (
    <>
      <ItemPriceRow
        description="Price before any discount"
        priceApi={itemPriceBeforeDiscount}
      />

      {!!containerTypeDiscountAmount && (
        <ItemPriceRow
          description="Price with container type discount"
          priceApi={priceWithContainerTypeDiscount}
          style={containerTypeDiscountIsBigger ? "bold" : "strike-through"}
        />
      )}

      {!!productItemDiscountAmount && (
        <ItemPriceRow
          description="Price with item discount"
          priceApi={priceWithProductItemDiscount}
          style={containerTypeDiscountIsBigger ? "strike-through" : "bold"}
        />
      )}
    </>
  );
};

export const ItemDiscountForm = ({
  onRemoveItem,
  onSubmit,
  productName,
  itemDiscount,
  itemPriceBeforeDiscount,
  containerTypeDiscountAmount,
  children,
}: {
  onRemoveItem?: () => void;
  onSubmit: (itemDiscount: ItemDiscountFormSubmitType) => void;
  productName: string;
  itemPriceBeforeDiscount: number;
  itemDiscount?: ItemDiscountType;
  containerTypeDiscountAmount: number;
  children?: ReactNode;
}) => {
  const { register, handleSubmit, control, reset } = useForm<FormInputs>({
    shouldUseNativeValidation: true,
    defaultValues: {
      type: "flat",
    },
  });

  const { type } = useWatch({
    control,
  });

  const productItemDiscountAmountPrice =
    useCalculateProductItemDiscountAmountApi({
      control,
      priceApi: itemPriceBeforeDiscount,
    });

  useEffect(() => {
    if (itemDiscount) {
      reset({
        ...itemDiscount,
        discountAmount:
          itemDiscount.type === "flat"
            ? parseFloat(formatPriceApiToInput(itemDiscount.discountAmount))
            : itemDiscount.discountAmount,
      });
    }
  }, [reset, itemDiscount]);

  return (
    <Form
      className={"flex flex-col gap-4"}
      onSubmit={handleSubmit((data) => {
        onSubmit({
          ...data,
          discountAmount:
            data.type === "flat"
              ? (formatPriceInputToApi(data.discountAmount) ?? 0)
              : data.discountAmount,
        });
      })}
    >
      <div
        className={"divide-y overflow-hidden rounded-lg border border-solid"}
      >
        <div className={"flex items-center justify-between p-4"}>
          <div className={"flex items-center gap-2"}>{productName}</div>
          {onRemoveItem && (
            <Button onClick={onRemoveItem} color={"secondary"} size={"small"}>
              <Icon icon={"CLOSE"} />
            </Button>
          )}
        </div>

        <div className={"flex items-center justify-between p-4"}>
          <div>Discount type</div>
          <div className="w-60">
            <InputSelect {...register("type", { required: true })}>
              <InputOption value={"flat"}>Flat</InputOption>
              <InputOption value={"percentage"}>Percentage</InputOption>
            </InputSelect>
          </div>
        </div>

        <div className={"flex items-center justify-between p-4"}>
          <div>Discount amount ({type === "flat" ? "£" : "%"})</div>
          <div className="w-60">
            <InputContainer>
              {type === "flat" ? (
                <InputCurrency
                  showPrefix
                  allowNegative
                  {...register("discountAmount", {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                  })}
                />
              ) : (
                <InputNumber
                  {...register("discountAmount", {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                  })}
                  step={"0.01"}
                />
              )}
            </InputContainer>

            <DiscountLeftCaption
              productItemDiscountAmount={productItemDiscountAmountPrice}
              containerTypeDiscountAmount={containerTypeDiscountAmount}
            />
          </div>
        </div>

        <ItemPricePreview
          itemPriceBeforeDiscount={itemPriceBeforeDiscount}
          containerTypeDiscountAmount={containerTypeDiscountAmount}
          productItemDiscountAmount={productItemDiscountAmountPrice}
        />
      </div>

      {children}
    </Form>
  );
};
